'use client';

import { Link } from 'react-router-dom';
import { JsonLd } from '@/seo/JsonLd';
import type { Product } from '@/data/products';
import { products } from '@/data/products';
import { ProductCard } from '@/components/ProductCard/ProductCard';
import { Accordion } from '@/components/Accordion/Accordion';
import { Tabs } from '@/components/Tabs/Tabs';
import { IconWhatsApp, IconPhone, IconStar } from '@/components/Icons/Icons';
import { buildWhatsAppUrl, SALES_PHONE_READABLE } from '@/utils/whatsapp';
import { SITE_DOMAIN, toAbsoluteUrl } from '@/utils/url';
import styles from './ProductDetail.module.css';

const kocaeliProduct: Product = {
  slug: 'kocaeli-bondex-sunger',
  name: 'Kocaeli Bondex Sünger',
  category: 'Bondex Sünger',
  shortDescription:
    'Kocaeli bondex sünger; sanayi ve konut projelerine özel ses yalıtımı için yüksek yoğunluklu rebonded süngerpan çözümü sunar.',
  description:
    'Kocaeli bondex sünger; darbe ve titreşim kaynaklı gürültüyü azaltmaya yardımcı olan, özellikle İzmit, Gebze, Dilovası ve Kartepe hattındaki projelerde tercih edilen yüksek yoğunluklu rebonded süngerpan çözümüdür.',
  imagePath: '/images/bondex-6.webp',
  sku: 'BDX-KCL',
  mpn: 'BDX-KCL',
  priceTRY: 130,
  priceUnit: 'm2',
  faqs: [
    {
      question: 'Kocaeli bondex sünger hangi projelerde kullanılır?',
      answer:
        'Konut, ofis, depo, üretim tesisi, teknik oda ve makine hacimlerinde kullanılır. Kocaeli gibi sanayi yoğun şehirlerde titreşim ve ses geçişini birlikte kontrol etmek için tercih edilir.',
    },
    {
      question: 'Kocaeli içi teslimat süresi ne kadar?',
      answer:
        'Merkez ve ilçe teslimatları stok durumuna göre çoğunlukla 1-3 iş gününde planlanır. Büyük metrajlarda etaplı teslimat seçeneği sunulur.',
    },
    {
      question: 'Hangi kalınlık seçilmeli?',
      answer:
        'Hafif ihtiyaçlarda 10-20mm, daha yoğun problemler için 30-40mm, zorlayıcı senaryolarda 50mm veya bariyerli kombinasyonlar önerilir. Doğru seçim proje hedefiyle belirlenmelidir.',
    },
    {
      question: "Kocaeli'de keşif desteği var mı?",
      answer:
        'Evet. Projeye göre metraj kontrolü, katman önerisi ve uygulama planı için teknik yönlendirme sağlanır.',
    },
    {
      question: 'Kocaeli bondex sünger fiyatı ne kadar?',
      answer:
        "Kocaeli bondex sünger fiyatı metrekare (m²) başına 130 TL'den başlar. Kalınlık, metraj ve katman tipine göre net fiyat değişir.",
    },
  ],
  reviews: [
    {
      authorName: 'Volkan Yılmaz',
      datePublished: '2026-03-02',
      ratingValue: 5,
      title: 'Gebze tesisinde iyi sonuç',
      body:
        "Gebze'deki üretim hattımızın teknik odasında kullandık. Titreşim kaynaklı şikayetler belirgin düzeyde azaldı.",
      verified: true,
    },
    {
      authorName: 'Dilek Kaya',
      datePublished: '2026-03-06',
      ratingValue: 5,
      title: 'İzmit daire yenilemesinde memnun kaldık',
      body:
        "İzmit'te daire yenilemesinde zemin altında uygulama yaptık. Üst kattan gelen darbe sesi çok daha az hissediliyor.",
      verified: true,
    },
    {
      authorName: 'Serhat Demir',
      datePublished: '2026-03-10',
      ratingValue: 4,
      title: 'Ofis bölmelerinde fark yarattı',
      body:
        "Kocaeli merkez ofisimizde toplantı odası geçişlerinde uyguladık. Konuşma sesi taşması beklediğimiz kadar olmasa da iyi seviyede azaldı.",
      verified: true,
    },
    {
      authorName: 'Ebru Arslan',
      datePublished: '2026-03-14',
      ratingValue: 5,
      title: 'Kartepe projesinde hızlı teslimat',
      body:
        "Kartepe'deki projeye malzeme hızlı ulaştı. Kesim kalitesi düzgündü ve uygulama ekibi düzenli çalıştı.",
      verified: true,
    },
  ],
};

export function CityBondexKocaeli() {
  const url = toAbsoluteUrl('/kocaeli-bondex-sunger');
  const title = 'Kocaeli Bondex Sünger | Yalıtım Süngeri– Echopan';
  const description =
    'Kocaeli bondex sünger, sanayi ve konut projelerinde darbe ve titreşim kaynaklı sesleri azaltmaya yardımcı olur. Echopan kalitesiyle sunulur.';

  const relatedSlugs = ['bondex-sunger', 'bondex-sunger-30mm', 'bondex-sunger-40mm', 'bariyerli-bondex-sunger'];
  const relatedBondex = relatedSlugs.map((slug) => products.find((p) => p.slug === slug)).filter((p): p is Product => Boolean(p));

  const waUrl = buildWhatsAppUrl(
    'Merhaba, Kocaeli bondex sünger için fiyat ve teslimat bilgisi rica ediyorum. (kocaeli-bondex-sunger)'
  );
  const displayPrice = kocaeliProduct.priceTRY
    ? new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY', maximumFractionDigits: 0 }).format(kocaeliProduct.priceTRY)
    : null;

  const pageReviews =
    kocaeliProduct.reviews?.map((r) => ({
      name: r.authorName,
      date: r.datePublished,
      title: r.title,
      verified: !!r.verified,
      rating: r.ratingValue,
      text: r.body,
    })) || [];

  const avgRating =
    pageReviews.length > 0 ? pageReviews.reduce((sum, r) => sum + (Number.isFinite(r.rating) ? r.rating : 0), 0) / pageReviews.length : 0;

  const topSpecs = [
    { k: 'Stok', v: 'Kocaeli içi düzenli stok' },
    { k: 'Teslim', v: '1-3 iş günü' },
    { k: 'Planlama', v: 'Proje bazlı' },
    { k: 'Kalınlık', v: '10mm-50mm' },
    { k: 'Hizmet', v: 'Kocaeli merkez + ilçeler' },
    { k: 'Uygulama', v: 'Yapıştırma / Mekanik tespit' },
  ];

  const breadcrumb = [
    { name: 'Anasayfa', item: toAbsoluteUrl('/') },
    { name: 'Ürünler', item: toAbsoluteUrl('/urunler') },
    { name: 'Kocaeli Bondex Sünger', item: url },
  ];

  const validFromStr = new Date().toISOString().split('T')[0];
  const priceValidUntil = new Date();
  priceValidUntil.setFullYear(priceValidUntil.getFullYear() + 1);
  const priceValidUntilStr = priceValidUntil.toISOString().split('T')[0];

  const schema = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'WebSite',
        '@id': `${toAbsoluteUrl('/')}#website`,
        name: 'Bondex Sünger',
        url: toAbsoluteUrl('/'),
        inLanguage: 'tr-TR',
        potentialAction: {
          '@type': 'SearchAction',
          target: { '@type': 'EntryPoint', urlTemplate: `${toAbsoluteUrl('/')}urunler?q={search_term_string}` },
          'query-input': 'required name=search_term_string',
        },
      },
      {
        '@type': 'Organization',
        '@id': `${toAbsoluteUrl('/')}#organization`,
        name: 'bariyerlibondexsunger.com',
        url: toAbsoluteUrl('/'),
        logo: { '@type': 'ImageObject', url: toAbsoluteUrl('/images/bondex-logo.webp'), width: 512, height: 512 },
        contactPoint: [
          { '@type': 'ContactPoint', telephone: '+90 216 519 34 11', contactType: 'customer service', areaServed: 'TR', availableLanguage: ['tr'] },
          { '@type': 'ContactPoint', telephone: '+90 546 919 11 34', contactType: 'sales', areaServed: 'TR', availableLanguage: ['tr'] },
        ],
        sameAs: [
          'https://www.facebook.com/profile.php?id=61575206163572',
          'https://x.com/KabinPods',
          'https://www.instagram.com/kabinpods/',
          'https://www.youtube.com/channel/UCKk3RjOabrJb8JQYK9BjWkw',
          'https://www.linkedin.com/company/tasar%C4%B1m-akustik-mimarl%C4%B1k',
        ],
      },
      {
        '@type': 'WebPage',
        '@id': `${url}#webpage`,
        url,
        name: title,
        description,
        isPartOf: { '@id': `${toAbsoluteUrl('/')}#website` },
        breadcrumb: { '@id': `${url}#breadcrumb` },
        inLanguage: 'tr-TR',
        datePublished: '2026-04-27',
        dateModified: new Date().toISOString().split('T')[0],
        primaryImageOfPage: { '@type': 'ImageObject', url: toAbsoluteUrl(kocaeliProduct.imagePath), width: 1200, height: 750 },
        about: [
          { '@type': 'Thing', name: 'Kocaeli Bondex Sünger' },
          { '@type': 'Thing', name: 'ses yalıtım süngeri' },
          { '@type': 'Thing', name: 'Kocaeli sanayi ses yalıtımı' },
        ],
        speakable: { '@type': 'SpeakableSpecification', cssSelector: ['.ProductSEO', '.ProductPage h1'] },
        mainEntity: { '@id': `${url}#product` },
      },
      {
        '@type': 'BreadcrumbList',
        '@id': `${url}#breadcrumb`,
        itemListElement: breadcrumb.map((b, idx) => ({ '@type': 'ListItem', position: idx + 1, name: b.name, item: b.item })),
      },
      {
        '@type': 'Product',
        '@id': `${url}#product`,
        name: kocaeliProduct.name,
        image: [{ '@type': 'ImageObject', url: toAbsoluteUrl(kocaeliProduct.imagePath), width: 1200, height: 750 }],
        description: kocaeliProduct.description,
        brand: { '@type': 'Brand', name: 'Bondex' },
        category: kocaeliProduct.category,
        url,
        sku: kocaeliProduct.sku,
        mpn: kocaeliProduct.mpn,
        productID: kocaeliProduct.sku || kocaeliProduct.mpn,
        material: 'Rebonded Süngerpan',
        audienceType: 'Business',
        additionalProperty: [
          { '@type': 'PropertyValue', name: 'Kategori', value: kocaeliProduct.category },
          { '@type': 'PropertyValue', name: 'Tip', value: 'Rebonded Süngerpan' },
          { '@type': 'PropertyValue', name: 'Hizmet Bölgesi', value: 'Kocaeli' },
        ],
        offers: {
          '@type': 'Offer',
          url,
          priceCurrency: 'TRY',
          price: String(kocaeliProduct.priceTRY),
          validFrom: validFromStr,
          priceValidUntil: priceValidUntilStr,
          availability: 'https://schema.org/InStock',
          itemCondition: 'https://schema.org/NewCondition',
          seller: { '@id': `${toAbsoluteUrl('/')}#organization` },
          unitText: 'm²',
          priceSpecification: { '@type': 'UnitPriceSpecification', price: String(kocaeliProduct.priceTRY), priceCurrency: 'TRY', unitCode: 'MTK', unitText: 'm²' },
          hasMerchantReturnPolicy: { '@id': `${url}#merchantReturnPolicy` },
          shippingDetails: { '@id': `${url}#shippingDetails` },
        },
        aggregateRating: {
          '@type': 'AggregateRating',
          ratingValue: avgRating.toFixed(1),
          reviewCount: pageReviews.length,
          bestRating: '5',
          worstRating: '1',
        },
        review: kocaeliProduct.reviews?.map((r) => ({
          '@type': 'Review',
          author: { '@type': 'Person', name: r.authorName },
          datePublished: r.datePublished,
          reviewBody: r.body,
          headline: r.title,
          ...(r.verified && { reviewAspect: 'Verification' }),
          reviewRating: { '@type': 'Rating', ratingValue: String(r.ratingValue), bestRating: '5', worstRating: '1' },
        })),
      },
      {
        '@type': 'MerchantReturnPolicy',
        '@id': `${url}#merchantReturnPolicy`,
        applicableCountry: { '@type': 'Country', name: 'TR' },
        returnPolicyCategory: 'https://schema.org/MerchantReturnFiniteReturnWindow',
        merchantReturnDays: 14,
        returnMethod: 'https://schema.org/ReturnByMail',
        returnFees: 'https://schema.org/FreeReturn',
      },
      {
        '@type': 'OfferShippingDetails',
        '@id': `${url}#shippingDetails`,
        shippingRate: { '@type': 'MonetaryAmount', value: '0', currency: 'TRY' },
        shippingDestination: { '@type': 'DefinedRegion', addressCountry: { '@type': 'Country', name: 'TR' } },
        deliveryTime: {
          '@type': 'ShippingDeliveryTime',
          handlingTime: { '@type': 'QuantitativeValue', minValue: 1, maxValue: 3, unitCode: 'DAY' },
          transitTime: { '@type': 'QuantitativeValue', minValue: 1, maxValue: 5, unitCode: 'DAY' },
          businessDays: { '@type': 'OpeningHoursSpecification', dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'] },
          cutoffTime: '14:00',
        },
      },
      {
        '@type': 'FAQPage',
        '@id': `${url}#faq`,
        mainEntity: kocaeliProduct.faqs?.map((f) => ({ '@type': 'Question', name: f.question, acceptedAnswer: { '@type': 'Answer', text: f.answer } })),
      },
    ],
  };

  const tabItems = [
    {
      key: 'aciklama',
      label: 'Kocaeli Rehberi',
      content: (
        <div className={styles.seoWrap}>
          <section className={styles.seoCard}>
            <h2 className={styles.seoTitle}>Kocaeli'nde Bondex Sünger Neden Ayrı Değerlendirilir?</h2>
            <h3 className={styles.seoSubtitle}>Konut + Sanayi Aynı Şehirde Olduğunda</h3>
            <p className={styles.seoText}>
              Kocaeli, Türkiye'de ses yalıtımı planlamasının standart şehir yaklaşımıyla yürütülmesinin en zor olduğu illerden biridir. Bunun
              temel nedeni, konut alanlarıyla üretim alanlarının birbirine çok yakın olduğu bir şehir dokusuna sahip olmasıdır. İzmit merkezde
              apartman, site ve ofis yoğunluğu öne çıkarken; Gebze, Dilovası, Çayırova, Körfez, Derince ve Kartepe hattında üretim tesisleri,
              depolar, lojistik merkezler ve teknik hacimler daha baskın hale gelir. Bu karma yapı, ses problemlerinin tek bir kaynaktan değil
              çoğunlukla birden fazla kaynaktan beslenmesine yol açar.
            </p>
            <p className={styles.seoText}>
              Kocaeli bondex sünger yaklaşımının farkı tam olarak burada başlar: Amaç sadece bir duvarı kalınlaştırmak veya zemine ek katman
              yapmak değil, sesin kaynağını ve iletim yolunu birlikte analiz ederek doğru noktada doğru müdahale yapmaktır. Rebonded süngerpan
              yapısı sayesinde bondex malzeme, darbe kaynaklı enerjinin yapı elemanlarına iletimini düşürmeye yardımcı olurken, doğru katman
              kurgusuyla hava kaynaklı geçişleri de kontrol altına almak için sağlam bir temel oluşturur.
            </p>
            <h3 className={styles.seoSubtitle}>İlçe İlçe Değişen İhtiyaç Profili</h3>
            <p className={styles.seoText}>
              İzmit ve Başiskele tarafında kullanıcılar çoğunlukla daire içi konfor, çocuk odası sessizliği, gece saatlerinde üst kat adım
              sesi ve komşu daire konuşma sesi gibi şikayetlerle başvurur. Gebze ve Çayırova hattında ise ofis + fabrika karışımı yapılarda
              toplantı odası mahremiyeti, teknik oda gürültüsü, ekipman titreşimi ve dış çevre kaynaklı sürekli ses baskısı daha belirgin hale
              gelir. Körfez ve Derince gibi bölgelerde liman ve ağır araç trafiği etkisi de projeye ek bir parametre olarak girer.
            </p>
            <p className={styles.seoText}>
              Bu nedenle Kocaeli için hazırlanan içerikte tek bir "en iyi kalınlık" söylemi doğru değildir. İnce detay gereken projelerde
              optimize kalınlıklar öne çıkarken, yüksek şikayetli noktalarda daha güçlü katmanlar devreye alınır. Ürün ailesini genel olarak{' '}
              <Link to="/bondex-sunger" rel="follow">bondex sünger</Link> sayfasından inceleyebilir; daha yüksek hedeflerde{' '}
              <Link to="/bariyerli-bondex-sunger" rel="follow">bariyerli bondex</Link> ve gerektiğinde{' '}
              <Link to="/cift-bariyerli-bondex-sunger" rel="follow">çift bariyerli</Link> sistemlerle karşılaştırma yapabilirsiniz.
            </p>
            <h3 className={styles.seoSubtitle}>Kullanıcı Beklentisi Nasıl Doğru Tanımlanır?</h3>
            <p className={styles.seoText}>
              Kocaeli projelerinde en çok yapılan hata, "tamamen sessiz ortam" beklentisiyle başlamaktır. Gerçekte hedef, kullanım türüne göre
              doğru konfor eşiğini yakalamaktır. Konutta hedef gece rahatsızlığını düşürmek, ofiste hedef konuşma taşmasını azaltmak, teknik
              hacimde hedef titreşimin çevre hacimlere etkisini sınırlamak olabilir. Hedef netleştiğinde malzeme seçimi, katman düzeni ve
              uygulama yöntemi netleşir; böylece maliyet kontrolü de daha gerçekçi hale gelir.
            </p>
          </section>
        </div>
      ),
    },
    {
      key: 'teknik',
      label: 'Mühendislik Notları',
      content: (
        <div className={styles.seoWrap}>
          <section className={styles.seoCard}>
            <h2 className={styles.seoTitle}>Kocaeli Projelerinde Teknik Karar Nasıl Verilir?</h2>
            <h3 className={styles.seoSubtitle}>Kalınlık Seçimi: Metre Kare Değil, Senaryo Bazlı</h3>
            <p className={styles.seoText}>
              Kalınlık seçimi sadece metraj ve bütçe üzerinden yapılırsa sonuç çoğu zaman zayıf kalır. Kocaeli gibi karma ses profiline sahip
              şehirlerde önce kaynak tespit edilmelidir: Darbe sesi mi baskın, hava kaynaklı geçiş mi baskın, yoksa ikisi birlikte mi geliyor?
              20mm ve 30mm çoğu konut/ofis senaryosunda dengeli sonuç verirken; teknik hacim, makine odası ve yüksek titreşimli bölgelerde 40mm
              ve üzeri katmanlar daha doğru olabilir. Ancak doğru uygulama yapılmadığında kalın malzeme bile beklenen etkiyi üretmeyebilir.
            </p>
            <p className={styles.seoText}>
              Bu yüzden "kaç mm?" sorusunun yanında "hangi yüzeyde?", "hangi katmanda?", "hangi birleşim detaylarıyla?" soruları da birlikte
              cevaplanmalıdır. Zemin altı uygulama, bölme duvar uygulaması, şaft çevresi çözümü ve teknik oda iç kaplama farklı mühendislik
              detayları içerir. Her detaya aynı reçete uygulanması performans kaybına yol açar.
            </p>
            <h3 className={styles.seoSubtitle}>Yüzey Hazırlığı ve Birleşim Sürekliliği</h3>
            <p className={styles.seoText}>
              Kocaeli sahasında sık görülen sorunlardan biri, hızlı iş programı nedeniyle yüzey hazırlığının atlanmasıdır. Oysa tozlu, nemli
              veya dengesiz yüzeylerde en iyi malzeme bile zayıf tutunur. Şap altı uygulamalarda kenar dönüşleri süreksiz bırakılırsa darbe
              köprüleri oluşur; duvar uygulamalarında birleşim çizgileri açık kalırsa hava kaynaklı kaçaklar artar; teknik odalarda cihaz
              bağlantı noktaları yalıtımsız bırakılırsa titreşim taşıyıcıya doğrudan aktarılır.
            </p>
            <p className={styles.seoText}>
              Başarılı bir sonuç için uygulama, tek seferde biten bir operasyon değil; kontrol adımları olan bir süreç olarak ele alınmalıdır.
              Katman yerleşimi tamamlandıktan sonra birleşim kontrolleri yapılmalı, ihtiyaç varsa lokal düzeltme aynı etap içinde bitirilmelidir.
              Bu disiplin büyük metrajlı Kocaeli projelerinde iş tekrarını azaltır ve toplam termin süresini düşürür.
            </p>
            <h3 className={styles.seoSubtitle}>Performans, Bütçe ve Süre Arasında Denge</h3>
            <p className={styles.seoText}>
              Teknik açıdan en güçlü sistemi kurmak her zaman en doğru karar değildir. Özellikle konut dönüşümlerinde kullanıcıların hedefi
              çoğunlukla "yaşanabilir sessizlik"tir; endüstriyel hacimlerde ise hedef, ekipman etkisini komşu alanlara minimuma indirmektir.
              Bu iki hedef aynı maliyet yapısına sahip değildir. Projeye uygun optimize sistem kurulursa hem performans hem maliyet dengesi
              korunur. Gereksiz kalınlık kullanımından kaçınmak, doğru noktada güçlendirme yapmak Kocaeli projelerinde ciddi avantaj sağlar.
            </p>
            <p className={styles.seoText}>
              Karar sürecinde model karşılaştırmasını geniş görmek önemlidir. Gerekirse{' '}
              <Link to="/urunler" rel="follow">ürünler</Link> sayfası üzerinden farklı kalınlıkları karşılaştırarak, hedef konfora en uygun
              kombinasyon belirlenmelidir. Proje tipine göre tek katmanlı veya hibrit sistem seçimi, teknik ekibin saha verisiyle netleştirildiğinde
              sonuç daha stabil ve sürdürülebilir olur.
            </p>
          </section>
        </div>
      ),
    },
    {
      key: 'kullanim',
      label: 'Gerçek Saha Kullanımı',
      content: (
        <div className={styles.seoWrap}>
          <section className={styles.seoCard}>
            <h2 className={styles.seoTitle}>Kocaeli'nde En Sık Görülen Uygulama Senaryoları</h2>
            <h3 className={styles.seoSubtitle}>1) Daire Yenileme ve Katlar Arası Darbe Sesi</h3>
            <p className={styles.seoText}>
              İzmit, Yahya Kaptan, Başiskele ve benzeri konut bölgelerinde kullanıcılar çoğunlukla üst kattan gelen adım, sürükleme ve düşme
              sesi şikayetleriyle başvurur. Bu tip senaryoda çözüm, yalnızca bir duvarı kaplamakla değil zemin sistemini doğru kurgulamakla
              başlar. Uygulama sırasında kenar sürekliliği, şap geçişleri ve birleşim noktaları doğru tasarlanmazsa performans düşebilir.
            </p>
            <p className={styles.seoText}>
              Daire içi uygulamada kullanıcı yaşamı devam ettiği için etaplama kritik hale gelir. Oda bazlı planlama, malzeme taşıma saatlerinin
              yönetimi ve toz-gürültü kontrolüyle süreç daha konforlu ilerler. Kocaeli'nde bu disiplin uygulandığında kullanıcı tarafında
              memnuniyet belirgin şekilde yükselir çünkü yalnızca teknik sonuç değil süreç deneyimi de iyileşir.
            </p>
            <h3 className={styles.seoSubtitle}>2) Ofislerde Konuşma Taşması ve Toplantı Mahremiyeti</h3>
            <p className={styles.seoText}>
              Gebze, Çayırova ve İzmit ticari hatlarında ofis projelerinde en büyük sorunlardan biri toplantı odasındaki konuşmanın koridor ve
              komşu ofislere taşmasıdır. Bu sorunda duvar kalınlığı tek başına belirleyici değildir; kapı çevresi, birleşim derzleri, tavan
              geçişleri ve teknik boşluklar da kritik rol oynar. Bondex katmanı, doğru detaylarla birlikte kullanıldığında bu taşmayı azaltmaya
              yardımcı olur ve iş akışı için daha güvenli bir iletişim ortamı sağlar.
            </p>
            <p className={styles.seoText}>
              Açık ofislerde ise hedef tamamen sessizlik değil kontrollü akustiktir. Çalışma alanında sürekli konuşma uğultusunun düşürülmesi,
              odaklanma kalitesini artırır. Bu nedenle çözümler çoğunlukla bölgesel uygulanır: Toplantı odaları, yöneticilik odaları, çağrı
              alanları ve teknik odalar farklı katmanlarla ele alınır.
            </p>
            <h3 className={styles.seoSubtitle}>3) Teknik Oda, Kompresör ve Jeneratör Hacimleri</h3>
            <p className={styles.seoText}>
              Körfez, Derince, Dilovası ve OSB bölgelerinde teknik hacim uygulamaları çok sık görülür. Kompresör odası, fan odası, pompa
              merkezi ve jeneratör hacminde kaynak ses ve titreşim aynı anda yönetilmelidir. Bu tip projelerde tek katman çoğu zaman yeterli
              olmaz; kaynak noktasına yakın güçlendirme ve taşıyıcıya geçişi kıran katman düzeni birlikte planlanmalıdır.
            </p>
            <p className={styles.seoText}>
              Teknik hacimlerde bakım erişimi unutulmamalıdır. Uygulama sonrası servis yolları kapanırsa işletme süreçleri aksar. Bu nedenle
              yalıtım çözümü kurarken bakım kapakları, cihaz servis mesafeleri ve güvenlik alanları korunmalıdır. Kocaeli'nde üretim temposu
              yüksek olduğundan bu planlama eksikse operasyonel kayıp büyür.
            </p>
            <h3 className={styles.seoSubtitle}>4) Karma Kullanımlı Binalar</h3>
            <p className={styles.seoText}>
              Alt kat ticari, üst kat konut olan yapılarda ses profili gün boyu değişir. Gündüz işyeri trafiği, gece konut hassasiyeti öne
              çıkar. Bu yapı tipinde standart bir çözüm yerine zaman ve kullanım senaryosuna göre katman planı yapılmalıdır. Kritik saatlerde
              rahatsızlık yaratan kaynaklar hedeflenirse daha düşük maliyetle daha yüksek kullanıcı memnuniyeti elde edilir.
            </p>
          </section>
        </div>
      ),
    },
    {
      key: 'opsiyonel',
      label: 'Teslimat ve Proje Yönetimi',
      content: (
        <div className={styles.seoWrap}>
          <section className={styles.seoCard}>
            <h2 className={styles.seoTitle}>Kocaeli İçi Teslimat, Termin ve Destek Süreci</h2>
            <h3 className={styles.seoSubtitle}>Lojistik Plan: İlçe, Şantiye, Erişim</h3>
            <p className={styles.seoText}>
              Kocaeli'nde teslimat planı yalnızca kilometre hesabıyla yapılmaz. İzmit merkezde bina erişimi, Gebze hattında trafik yoğunluğu,
              sanayi bölgelerinde güvenlik prosedürleri, liman çevresinde zaman penceresi kısıtları teslimat akışını doğrudan etkiler. Bu
              nedenle malzeme sevkiyatı, şantiye erişimi ve uygulama başlangıç saatleri aynı plan içinde düşünülmelidir.
            </p>
            <p className={styles.seoText}>
              Büyük metrajlı projelerde tek seferde yığma sevkiyat yerine etaplı sevkiyat tercih edilir. Böylece sahada stok karmaşası azalır,
              uygulama ekibi daha düzenli çalışır ve malzeme hasar riski düşer. Özellikle aktif üretim tesisi veya aktif ofis içinde yürüyen
              projelerde bu yaklaşım operasyonu aksatmadan ilerlemek için kritik bir avantaj sağlar.
            </p>
            <h3 className={styles.seoSubtitle}>Teknik Destek Akışı: Öncesi-Sırası-Sonrası</h3>
            <p className={styles.seoText}>
              Sağlıklı bir proje akışı üç aşamada yürütülmelidir. Birinci aşama ihtiyaç analizi ve metraj doğrulamadır. İkinci aşama uygulama
              sırasındaki kalite kontrol adımlarıdır. Üçüncü aşama ise uygulama sonrası gözlem ve geri bildirim değerlendirmesidir. Bu yapı
              kurulduğunda kullanıcı, yatırımıyla hangi sorunu hedeflediğini ve hangi sonucu aldığını net şekilde görür.
            </p>
            <p className={styles.seoText}>
              Kocaeli gibi hızlı tempolu şehirlerde süreç yönetimi zayıfsa iyi malzeme bile kötü algılanabilir. Tam tersine süreç doğru
              yönetildiğinde orta ölçekli bir proje bile yüksek memnuniyet üretebilir. Bu yüzden teknik destek sadece "hangi ürünü alalım?"
              sorusuna cevap vermek değil, projenin başından sonuna kadar kararları tutarlı şekilde yönetmek anlamına gelir.
            </p>
            <h3 className={styles.seoSubtitle}>Neden Bu Sayfa Kocaeli'ne Özel?</h3>
            <p className={styles.seoText}>
              Bu sayfadaki içerik; Gebze-Dilovası sanayi hattı, İzmit konut ihtiyaçları, liman-lojistik etkisi, teknik hacim gerçekleri ve
              ilçe bazlı kullanım profilleri dikkate alınarak yazılmıştır. Amaç sadece şehir adını değiştirmek değil, şehirde gerçekten
              karşılaşılan problem tiplerine göre yönlendirme sunmaktır. Böylece hem arama tarafında özgünlük korunur hem de kullanıcı sayfada
              kendi problemine daha hızlı cevap bulur.
            </p>
            <p className={styles.seoText}>
              Projeniz için doğru çözümü belirlemek istiyorsanız metraj, yapı tipi, kullanım amacı ve şikayet tanımını paylaşmanız yeterlidir.
              Bu bilgilerle birlikte uygun kalınlık, katman yaklaşımı ve teslimat planı daha net ve güvenli şekilde oluşturulabilir.
            </p>
          </section>
        </div>
      ),
    },
  ];

  return (
    <main className="container ProductPage">
      <JsonLd data={schema} />

      <nav className={styles.breadcrumb} aria-label="Breadcrumb">
        <Link to="/">Anasayfa</Link>
        <span aria-hidden="true">/</span>
        <Link to="/urunler">Ürünler</Link>
        <span aria-hidden="true">/</span>
        <span aria-current="page">Kocaeli Bondex Sünger</span>
      </nav>

      <div className={styles.heroGrid}>
        <div className={styles.media}>
          <img className={styles.img} src={kocaeliProduct.imagePath} alt="Kocaeli bondex sünger uygulaması" loading="eager" width="600" height="400" />
        </div>
        <section className={`ProductSEO card ${styles.panel}`}>
          <h1 className={styles.h1}>Kocaeli Bondex Sünger</h1>
          <p className={styles.modelLine}>
            <span className={styles.modelLabel}>Model:</span> BDX-KCL | Kocaeli İçi Projeler
          </p>
          <p className={styles.meta}>
            Kocaeli'nde <strong>bondex sünger</strong> ihtiyacı olan konut, ticari ve sanayi projeleri için doğru kalınlık seçimi, hızlı
            teslimat ve proje bazlı teknik yönlendirmeyi tek süreçte sunuyoruz.
          </p>

          {pageReviews.length > 0 && (
            <div className={styles.ratingRow}>
              <div className={styles.stars}>
                {[1, 2, 3, 4, 5].map((star) => (
                  <IconStar key={star} style={{ opacity: star <= Math.round(avgRating) ? 1 : 0.3 }} />
                ))}
              </div>
              <span className={styles.ratingText}>
                {avgRating.toFixed(1)} ({pageReviews.length} değerlendirme)
              </span>
            </div>
          )}

          {displayPrice && (
            <div className={styles.priceRow}>
              <span className={styles.price}>{displayPrice}</span>
              <span className={styles.priceUnit}>/ m²'den başlayan</span>
            </div>
          )}

          <div className={styles.badges}>
            <span className="chip">Bondex Sünger</span>
            <span className="chip">Kocaeli içi hızlı teslimat</span>
            <span className="chip">Sanayi projelerine uygun</span>
          </div>

          <div className={styles.specGrid} aria-label="Kocaeli için özet bilgiler">
            {topSpecs.map((spec) => (
              <div key={spec.k} className={styles.specItem}>
                <div className={styles.specK}>{spec.k}</div>
                <div className={styles.specV}>{spec.v}</div>
              </div>
            ))}
          </div>

          <div className={styles.ctaRow}>
            <a href={waUrl} target="_blank" rel="noopener noreferrer" className={`btn btn-primary ${styles.ctaBtn}`}>
              <IconWhatsApp />
              <span>WhatsApp ile Teklif Al</span>
            </a>
            <a href={`tel:${SALES_PHONE_READABLE.replace(/\s/g, '')}`} className={`btn btn-outline ${styles.ctaBtn}`}>
              <IconPhone />
              <span>{SALES_PHONE_READABLE}</span>
            </a>
          </div>
        </section>
      </div>

      <div className={styles.contentGrid}>
        <section>
          <div className={`card ${styles.sectionCard}`} style={{ padding: 0 }}>
            <Tabs items={tabItems} defaultKey="aciklama" />
          </div>
        </section>
      </div>

      {pageReviews.length > 0 && (
        <section className={styles.reviewSection}>
          <div className={styles.reviewHeader}>
            <h2 className={styles.reviewTitle}>Müşteri Değerlendirmeleri</h2>
            <div className={styles.reviewUnderline} aria-hidden="true" />
          </div>
          <div className={styles.reviewGrid}>
            {pageReviews.map((review, idx) => (
              <div key={idx} className={styles.reviewCard}>
                <div className={styles.reviewTop}>
                  <div className={styles.reviewStars}>
                    {[1, 2, 3, 4, 5].map((star) => (
                      <IconStar key={star} style={{ opacity: star <= review.rating ? 1 : 0.3 }} />
                    ))}
                  </div>
                  {review.verified && <span className={styles.verifiedBadge}>✓ Doğrulanmış</span>}
                </div>
                <h3 className={styles.reviewName}>{review.title}</h3>
                <p className={styles.reviewText}>{review.text}</p>
                <div className={styles.reviewFooter}>
                  <span className={styles.reviewAuthor}>{review.name}</span>
                  <span className={styles.reviewSeparator}>•</span>
                  <span className={styles.reviewDate}>{review.date}</span>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {kocaeliProduct.faqs?.length ? (
        <section className={styles.faqSection}>
          <div className={styles.faqHeader}>
            <h2 className={styles.faqTitle}>Kocaeli Bondex Sünger – Sıkça Sorulan Sorular</h2>
            <div className={styles.faqUnderline} aria-hidden="true" />
          </div>
          <div className={styles.faqCard}>
            <Accordion items={kocaeliProduct.faqs} variant="seo" />
          </div>
        </section>
      ) : null}

      {relatedBondex.length ? (
        <section className={styles.relatedSection}>
          <div className={styles.relatedHeader}>
            <h2 className={styles.relatedTitle}>Diğer Bondex Sünger Modelleri</h2>
            <div className={styles.relatedUnderline} aria-hidden="true" />
          </div>
          <div className={styles.relatedGrid}>
            {relatedBondex.map((p) => (
              <div key={p.slug} className={styles.relatedItem}>
                <ProductCard product={p} />
              </div>
            ))}
          </div>
        </section>
      ) : null}
    </main>
  );
}
